/* GEOTECH · ARC mobile-first helpers
   Keeps mobile conversion controls available without duplicating Shopify product forms. */
(() => {
  if (window.__geoMobileFirst) return;
  window.__geoMobileFirst = true;

  const mq = window.matchMedia('(max-width: 749px)');
  let cleanup = null;

  const setupProductBuybar = () => {
    if (!mq.matches || document.querySelector('.geo-mobile-buybar')) return () => {};

    const productInfo = document.querySelector('product-info');
    if (!productInfo) return () => {};

    const getButton = () =>
      productInfo.querySelector('.gvp__notify-button:not([disabled])') ||
      productInfo.querySelector('.product-form__submit');
    if (!getButton()) return () => {};

    const bar = document.createElement('div');
    bar.className = 'geo-mobile-buybar';
    bar.setAttribute('aria-hidden', 'true');
    bar.setAttribute('inert', '');
    bar.innerHTML = '<div class="geo-mobile-buybar__price"></div><button type="button" class="geo-mobile-buybar__button"></button>';

    const price = bar.querySelector('.geo-mobile-buybar__price');
    const proxy = bar.querySelector('.geo-mobile-buybar__button');
    document.body.appendChild(bar);

    let observedButton = null;

    const setVisible = (visible) => {
      if (bar.classList.contains('is-visible') === visible) return;
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      bar.toggleAttribute('inert', !visible);
      if (!visible && bar.contains(document.activeElement)) {
        getButton()?.focus({ preventScroll: true });
      }
    };

    const io = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        setVisible(false);
        return;
      }
      // Show once the real button is above the viewport. (GEOTECH fix: no longer requires the
      // button to have been seen first — see checkPosition below.)
      setVisible(entry.boundingClientRect.top < 0);
    }, { threshold: .15 });

    // GEOTECH fix (Sep 2026): IntersectionObserver never fires when the page opens already scrolled
    // past the button (browser "back" restores the scroll position), so the bar stayed hidden.
    // Re-check the button's position on scroll (rAF-throttled) and when the page is shown.
    let positionFrame = 0;
    const checkPosition = () => {
      positionFrame = 0;
      const button = observedButton || getButton();
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const onScreen = rect.bottom > 0 && rect.top < window.innerHeight;
      setVisible(!onScreen && rect.top < 0);
    };
    const onScrollCheck = () => {
      if (!positionFrame) positionFrame = requestAnimationFrame(checkPosition);
    };
    window.addEventListener('scroll', onScrollCheck, { passive: true });
    window.addEventListener('pageshow', onScrollCheck);

    const observeCurrentButton = () => {
      const current = getButton();
      if (!current || current === observedButton) return;
      if (observedButton) io.unobserve(observedButton);
      observedButton = current;
      io.observe(observedButton);
    };

    const sync = () => {
      observeCurrentButton();
      const original = getButton();
      if (!original) return;

      const sourceLabel = original.querySelector('span')?.textContent?.trim() || original.textContent.trim() || 'Add to cart';
      const busy = original.getAttribute('aria-busy') === 'true' || original.classList.contains('loading');
      proxy.textContent = busy ? 'Adding…' : sourceLabel;
      proxy.disabled = original.disabled || original.getAttribute('aria-disabled') === 'true';
      proxy.toggleAttribute('aria-busy', busy);
      proxy.setAttribute('aria-label', proxy.textContent);

      const priceSource = productInfo.querySelector('.price--large') || productInfo.querySelector('[id^="price-"] .price');
      if (priceSource) {
        const current = priceSource.querySelector('.price-item--sale, .price-item--regular, .price-item') || priceSource;
        price.textContent = current.textContent.replace(/\s+/g, ' ').trim();
      }
    };

    proxy.addEventListener('click', () => {
      const original = getButton();
      if (!original || proxy.disabled) return;
      original.click();
      proxy.animate?.(
        [{ transform: 'scale(1)' }, { transform: 'scale(.985)' }, { transform: 'scale(1)' }],
        { duration: 110, easing: 'cubic-bezier(.2,.8,.2,1)' }
      );
    });

    const mo = new MutationObserver(() => requestAnimationFrame(sync));
    mo.observe(productInfo, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class']
    });

    sync();
    onScrollCheck();

    return () => {
      io.disconnect();
      mo.disconnect();
      window.removeEventListener('scroll', onScrollCheck);
      window.removeEventListener('pageshow', onScrollCheck);
      if (positionFrame) cancelAnimationFrame(positionFrame);
      bar.remove();
    };
  };

  const honorConstrainedData = () => {
    if (!mq.matches) return;
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c || (!c.saveData && !/(^|-)2g$/.test(c.effectiveType || ''))) return;
    document.querySelectorAll('video[autoplay]').forEach((video) => {
      video.pause();
      video.removeAttribute('autoplay');
      video.preload = 'none';
    });
  };

  const init = () => {
    cleanup?.();
    cleanup = null;
    if (mq.matches) cleanup = setupProductBuybar();
    honorConstrainedData();
  };

  const scheduleInit = () => requestAnimationFrame(init);
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', scheduleInit, { once: true })
    : scheduleInit();

  mq.addEventListener?.('change', scheduleInit);
  document.addEventListener('shopify:section:load', scheduleInit);
})();
